import React, { useMemo, useState } from "react";
import {
  Package,
  TruckIcon,
  MapPin,
  CheckCircle2,
  Circle,
  ArrowRight,
  Calculator,
  Receipt,
  TrendingDown
} from "lucide-react";

/* ===================== RFQ DATA ===================== */

const rfqItems = [
  { id: "I1", name: "Zuari OPC 53 Grade", qty: 20, weightPerUnit: 50, category: "Cement" },
  { id: "I2", name: 'Asian 5" Paint Brush', qty: 10, weightPerUnit: 0.3, category: "Paint Accessories" },
  { id: "I3", name: "150 No Jawan Water Sand Paper", qty: 10, weightPerUnit: 0.1, category: "Paint Accessories" },
  { id: "I4", name: "Amber Paint Roller", qty: 10, weightPerUnit: 0.5, category: "Paint Accessories" },
  { id: "I5", name: 'Putty Makku Blade 4"', qty: 10, weightPerUnit: 0.4, category: "Paint Accessories" },
  { id: "I6", name: "Roff Tile Adhesive T01 20KG", qty: 10, weightPerUnit: 20, category: "Tile Fixing Products" }
];

const suppliers = [
  {
    id: "S1",
    name: "Sri Venkateswara Traders",
    area: "Auto Nagar",
    distanceKm: 6,
    prices: { I1: 225, I2: 115, I3: 13, I4: 135, I6: 290 }
  },
  {
    id: "S2",
    name: "Krishna Building Supplies",
    area: "Gollapudi",
    distanceKm: 14,
    prices: { I1: 218, I2: 110, I3: 12, I5: 9, I6: 275 }
  },
  {
    id: "S3",
    name: "Local Paint & Hardware",
    area: "Benz Circle",
    distanceKm: 3,
    prices: { I2: 118, I3: 14, I4: 132, I5: 8 }
  },
  {
    id: "S4",
    name: "Capital Tiles & Adhesives",
    area: "Poranki",
    distanceKm: 18,
    prices: { I1: 230, I4: 138, I6: 285 }
  }
];

const vehicles = [
  { type: "Bike", maxWeight: 100, costPerKm: 8 },
  { type: "Auto", maxWeight: 1000, costPerKm: 15 },
  { type: "Pickup", maxWeight: 5000, costPerKm: 28 }
];

/* ===================== HELPERS ===================== */

function chooseVehicle(weight) {
  return vehicles.find(v => weight <= v.maxWeight) || vehicles[vehicles.length - 1];
}

/* ===================== COMPONENT ===================== */

export default function AdminAwardDecision() {
  const [selection, setSelection] = useState({});
  const [hoveredCell, setHoveredCell] = useState(null);

  /* -------- PRICE MATRIX -------- */
  const matrix = useMemo(() => {
    return rfqItems.map(item => {
      const prices = suppliers
        .filter(s => s.prices[item.id])
        .map(s => ({
          supplierId: s.id,
          unitPrice: s.prices[item.id],
          total: s.prices[item.id] * item.qty
        }));

      return {
        item,
        prices,
        min: Math.min(...prices.map(p => p.total)),
        max: Math.max(...prices.map(p => p.total))
      };
    });
  }, []);

  /* -------- SUMMARY -------- */
  const summary = useMemo(() => {
    let itemsTotal = 0;
    const supplierItems = {};

    Object.entries(selection).forEach(([itemId, supplierId]) => {
      const item = rfqItems.find(i => i.id === itemId);
      const supplier = suppliers.find(s => s.id === supplierId);
      if (!item || !supplier) return;

      itemsTotal += supplier.prices[itemId] * item.qty;
      supplierItems[supplierId] ||= [];
      supplierItems[supplierId].push(item);
    });

    let deliveryTotal = 0;
    const deliveries = [];

    Object.entries(supplierItems).forEach(([supplierId, items]) => {
      const supplier = suppliers.find(s => s.id === supplierId);
      if (!supplier) return;

      const totalWeight = items.reduce(
        (sum, i) => sum + i.qty * i.weightPerUnit,
        0
      );

      const vehicle = chooseVehicle(totalWeight);
      const cost = vehicle.costPerKm * supplier.distanceKm;

      deliveryTotal += cost;
      deliveries.push({
        supplier: supplier.name,
        vehicle: vehicle.type,
        distance: supplier.distanceKm,
        weight: totalWeight,
        itemCount: items.length,
        cost
      });
    });

    return {
      itemsTotal,
      deliveryTotal,
      grandTotal: itemsTotal + deliveryTotal,
      deliveries,
      selectedCount: Object.keys(selection).length,
      completionPercentage: Math.round(
        (Object.keys(selection).length / rfqItems.length) * 100
      )
    };
  }, [selection]);

  /* ===================== UI ===================== */

  return (
    <div className="min-h-screen bg-slate-100 p-8">
      <h1 className="text-2xl font-semibold mb-6">
        RFQ Award Decision – Admin View
      </h1>

      <div className="grid grid-cols-[2.2fr_1fr] gap-6">
        {/* ================= TABLE ================= */}
        <div className="bg-white rounded-xl border overflow-auto">
          <table className="w-full border-collapse">
            <thead className="bg-slate-900 text-white">
              <tr>
                <th className="text-left px-5 py-4">Item</th>
                {suppliers.map(s => (
                  <th key={s.id} className="px-5 py-4 text-center">
                    <div>{s.name}</div>
                    <div className="text-xs text-slate-300">
                      {s.area} • {s.distanceKm} km
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {matrix.map(row => (
                <tr key={row.item.id} className="border-b">
                  <td className="px-5 py-4">
                    <div className="font-medium">{row.item.name}</div>
                    <div className="text-xs text-slate-500">
                      Qty: {row.item.qty} • {row.item.category}
                    </div>
                  </td>

                  {suppliers.map(s => {
                    const price = row.prices.find(p => p.supplierId === s.id);
                    const isSelected = selection[row.item.id] === s.id;
                    const isBest = price && price.total === row.min;

                    if (!price) {
                      return (
                        <td key={s.id} className="text-center text-slate-300">
                          —
                        </td>
                      );
                    }

                    return (
                      <td
                        key={s.id}
                        className={`px-5 py-4 text-center cursor-pointer ${
                          isSelected ? "bg-blue-50" : ""
                        }`}
                        onClick={() =>
                          setSelection(p => ({ ...p, [row.item.id]: s.id }))
                        }
                        onMouseEnter={() =>
                          setHoveredCell(`${row.item.id}-${s.id}`)
                        }
                        onMouseLeave={() => setHoveredCell(null)}
                      >
                        <div className="flex flex-col items-center gap-1">
                          {isSelected ? (
                            <CheckCircle2 className="text-blue-600" />
                          ) : (
                            <Circle className="text-slate-300" />
                          )}
                          <div className="text-sm">
                            ₹{price.unitPrice} × {row.item.qty}
                          </div>
                          <div className="font-medium">
                            ₹{price.total}
                            {isBest && (
                              <span className="ml-2 text-xs text-green-600">
                                Best
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ================= SUMMARY ================= */}
        <div className="bg-slate-900 text-white rounded-xl p-6">
          <h3 className="flex items-center gap-2 mb-4">
            <Receipt /> Award Summary
          </h3>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span>Items Total</span>
              <span>₹{summary.itemsTotal}</span>
            </div>
            <div className="flex justify-between">
              <span>Delivery</span>
              <span>₹{summary.deliveryTotal}</span>
            </div>
          </div>

          <hr className="my-4 border-slate-700" />

          <div className="text-3xl font-semibold text-green-400">
            ₹{summary.grandTotal}
          </div>

          {summary.selectedCount === rfqItems.length && (
            <button className="mt-6 w-full bg-blue-600 hover:bg-blue-700 py-3 rounded-lg flex items-center justify-center gap-2">
              Finalize Award <ArrowRight />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
